import * as XLSX from 'xlsx';

// Reads the "PS Services" sheet of a PS Pricing Model workbook into budget-line candidates.
// Sheet layout: header row has "In Scope" in col C; each service row is name / In Scope / Dept /
// ... / Hours (H) / Revenue (K). Category names in the sheet are the BudgetCategory names, so rows
// match by name; anything unmatched is returned in `skipped` rather than guessed at.
const DEPT_TEAM = {
  pmo: 'PMO', valuation: 'Valuation', tax: 'Tax', 'public access': 'PA', pa: 'PA', ie: 'IE', 'aa/records': 'Assessment',
};

// Sheet row labels that differ from the BudgetCategory name.
const NAME_ALIAS = {
  'data conversion (mapping/coding/validation)': 'data conversion/migration',
  'ps ie (ops, interfaces, corres, workflows)': 'operations, interfaces, corres, workflows',
};

const norm =(s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();

export async function parseEstimateWorkbook(file, categories, teams) {
  const wb = XLSX.read(await file.arrayBuffer());
  const ws = wb.Sheets['PS Services'];
  if (!ws) throw new Error('No "PS Services" sheet found — is this a PS Pricing Model workbook?');
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

  const header = rows.findIndex((r) => norm(r[2]) === 'in scope');
  if (header < 0) throw new Error('Could not find the "In Scope" header row on the PS Services sheet.');

  const catByName = new Map(categories.map((c) => [norm(c.name), c]));
  const teamByName = new Map(teams.map((t) => [norm(t.name), t]));
  const lines = [];
  const skipped = [];
  const num = (v) => (Number.isFinite(Number(v)) && v !== '' ? Number(v) : 0);

  const push = (name, category, dept, hours, amount) => {
    if (!category) { skipped.push(`${name} (${Math.round(hours)} hrs) — no matching category`); return; }
    const teamName = category.name === 'Data Conversion/Migration' ? 'Imp Eng & Conversion' : DEPT_TEAM[norm(dept)];
    const team = teamName ? teamByName.get(norm(teamName)) : null;
    lines.push({
      name, categoryId: category.id, teamId: team?.id || null, teamName: team?.name || '',
      plannedHours: Math.round(hours * 100) / 100, plannedAmount: Math.round(amount * 100) / 100,
    });
  };

  for (const r of rows.slice(header + 1)) {
    const name = String(r[1] || '').trim();
    if (!name) continue;
    if (name.startsWith('Travel -')) {
      // The per-team travel rows below this header are a breakdown of the one "Travel --" summary
      // row, so stop here and read only the summary. Travel Total (col K) = travel-time revenue +
      // budgetary travel expense.
      const sum = rows.find((x) => String(x[1] || '').startsWith('Travel --'));
      const total = rows.find((x) => norm(x[9]) === 'travel total');
      if (sum && num(sum[7]) > 0) push('Travel', catByName.get('travel'), '', num(sum[7]), num(total?.[10]) || num(sum[10]));
      break;
    }
    if (norm(r[2]) !== 'yes') continue;
    const hours = num(r[7]);
    if (hours <= 0) continue;
    push(name, catByName.get(NAME_ALIAS[norm(name)] || norm(name)), r[3], hours, num(r[10]));
  }

  const county = String(rows[0]?.[1] || '').replace(/^County Name:\s*/i, '').trim();
  return { county, lines, skipped };
}
